/**
 * C-ORE — MIS PDF generator.
 *
 * `generateMisPdf(data)` renders a 1-3 page A4 MIS report with five
 * sections: Key Numbers · Profit & Loss · Cash Flow · Inventory · Priority
 * Actions.  Calling code passes the already-shaped data — see
 * `buildMisData()` for the helper that produces the shape from the live
 * `useCustomerData` hook.
 *
 * Every value comes from the customer's real data — missing fields are
 * rendered as the dash placeholder. No demo / sample fallback exists.
 *
 * jsPDF Helvetica is Latin-1 only — the ₹ glyph is replaced with "Rs."
 * everywhere so the PDF prints correctly without bundling a custom font.
 */
import { jsPDF } from "jspdf";

const NAVY  = [30, 58, 95];
const GREEN = [16, 185, 129];
const INK   = [15, 27, 45];
const INK2  = [55, 65, 81];
const MUTED = [156, 163, 175];
const RULE  = [226, 232, 240];
const ROSE  = [220, 38, 38];

const DASH = "-";

// jsPDF's Helvetica is Latin-1 only. Any character outside that range
// (₹ rupee, ’ curly quote, • bullet, narrow no-break space, etc.) renders
// as `(cid:0)` artefacts. We swap the common offenders before drawing.
function latin1Safe(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/\u20B9/g, "Rs. ")     // ₹
    .replace(/[\u2018\u2019]/g, "'") // curly single quotes
    .replace(/[\u201C\u201D]/g, '"') // curly double quotes
    .replace(/\u2013/g, "-")         // en dash
    .replace(/\u2014/g, "--")        // em dash
    .replace(/\u2022/g, "-")         // bullet
    .replace(/\u00A0/g, " ")         // non-breaking space
    .replace(/\u202F/g, " ")         // narrow no-break space
    .replace(/[^\x09\x0A\x0D\x20-\xFF]/g, "?"); // anything else → '?'
}

// ---------------------------------------------------------------------------
// Currency / number formatting (Indian style)
// ---------------------------------------------------------------------------
function fmtINR(rupees) {
  if (rupees === null || rupees === undefined || Number.isNaN(rupees)) return DASH;
  const n = Number(rupees);
  if (!Number.isFinite(n)) return DASH;
  const abs = Math.abs(n);
  if (abs >= 1_00_00_000) return `${n < 0 ? "-" : ""}Rs. ${(abs / 1_00_00_000).toFixed(2)}Cr`;
  if (abs >= 1_00_000)    return `${n < 0 ? "-" : ""}Rs. ${(abs / 1_00_000).toFixed(2)}L`;
  if (abs >= 1000)        return `${n < 0 ? "-" : ""}Rs. ${(abs / 1000).toFixed(1)}K`;
  return `${n < 0 ? "-" : ""}Rs. ${Math.round(abs).toLocaleString("en-IN")}`;
}

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  const n = Number(v);
  if (!Number.isFinite(n)) return DASH;
  return `${n.toFixed(1)}%`;
}

function fmtNum(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  const n = Number(v);
  if (!Number.isFinite(n)) return DASH;
  return Math.round(n).toLocaleString("en-IN");
}

function todayLabel() {
  return new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function currentPeriod() {
  return new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Public: build MIS payload from live customerData (useCustomerData shape).
// Returns `null` when no real data is available — the caller (TopBar) must
// gate the PDF generation accordingly. No demo / sample fallback exists.
// ---------------------------------------------------------------------------
export function buildMisData({ customerData, customer, integrationsConfidence }) {
  if (!customerData) {
    return null;
  }

  const liq    = customerData.liquidity     || {};
  const prof   = customerData.profitability || {};
  const gst    = customerData.gstAnalytics  || {};
  const inv    = customerData.inventoryAnalytics || {};
  const health = customerData.health        || {};
  const raw    = customerData.raw           || {};
  const fin    = customerData.financial     || {};
  const der    = customerData.derived       || {};

  const company_name =
    customerData.company_name ||
    customerData.meta?.tally_company ||
    customer?.company_name ||
    "Your business";

  const revenue   = fin.revenue_mtd  ?? null;
  const cogsRM    = fin.raw_material ?? raw.raw_material ?? null;
  const cogsDL    = fin.direct_labour ?? raw.direct_labour ?? null;
  const cogsOH    = fin.overhead     ?? raw.overhead     ?? null;
  const cogs_total = (cogsRM ?? 0) + (cogsDL ?? 0) + (cogsOH ?? 0);
  const grossProfit = revenue != null ? (revenue - cogs_total) : null;
  const opex      = (fin.expenses_mtd ?? null) != null && revenue != null
    ? Math.max(0, (fin.expenses_mtd) - cogs_total)
    : null;
  const netProfit = fin.net_profit ?? prof.net_profit ?? null;
  const netMargin = revenue && netProfit != null ? (netProfit / revenue) * 100 : null;

  return {
    company_name,
    period:       `MIS Report  -  ${currentPeriod()}`,
    is_demo:      false,
    confidence_pct: integrationsConfidence?.percent ?? null,
    key_numbers: [
      { label: "Cash Available", value: fmtINR(fin.cash ?? liq.cash) },
      { label: "Cash Runway",    value: der.cash_runway_days != null ? `${der.cash_runway_days} days` : DASH },
      { label: "Revenue MTD",    value: fmtINR(revenue) },
      { label: "Net Profit MTD", value: fmtINR(netProfit) },
      { label: "GST Payable",    value: fmtINR(fin.gst_payable ?? gst.gst_net_payable) },
      { label: "Health Score",   value: der.health_score != null ? `${der.health_score} / 100`
                                       : health.health_score != null ? `${health.health_score} / 100` : DASH },
    ],
    pnl: {
      revenue,
      raw_material:      cogsRM,
      direct_labour:     cogsDL,
      overhead:          cogsOH,
      gross_profit:      grossProfit,
      gross_margin_pct:  prof.gross_margin_pct ?? null,
      opex,
      net_profit:        netProfit,
      net_margin_pct:    netMargin,
    },
    cash_flow: {
      opening_cash: null,   // not surfaced by the engine yet
      inflows:      revenue,
      outflows:     fin.expenses_mtd ?? null,
      closing_cash: fin.cash ?? liq.cash ?? null,
      runway_days:  der.cash_runway_days ?? null,
      gst_payable:  fin.gst_payable ?? gst.gst_net_payable ?? null,
    },
    inventory: {
      items: Array.isArray(customerData.inventoryItems)
        ? customerData.inventoryItems.slice(0, 6).map((it) => ({
            name:        it.name || it.item_name || it.product_name || DASH,
            qty:         it.qty ?? it.quantity ?? null,
            unit:        it.unit || "",
            value:       it.value ?? it.stock_value ?? null,
            days_cover:  it.days_cover ?? it.days_of_cover ?? null,
          }))
        : [],
      total_value:    inv.total_value ?? null,
      days_cover_avg: inv.days_cover_avg ?? null,
    },
    priority_actions: Array.isArray(customerData.alerts) && customerData.alerts.length > 0
      ? customerData.alerts.slice(0, 5).map((a) => ({
          title:  a.title || a.message || "Action",
          detail: a.detail || a.body || a.description || "",
        }))
      : [],
  };
}

// ---------------------------------------------------------------------------
// PDF renderer
// ---------------------------------------------------------------------------
function drawSectionHeader(doc, label, y, margin, pageW) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(latin1Safe(label.toUpperCase()), margin, y);
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.6);
  doc.line(margin, y + 4, pageW - margin, y + 4);
  return y + 22;
}

function ensureSpace(doc, y, needed, margin, pageW) {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + needed > pageH - 60) {
    doc.addPage();
    return 56;
  }
  return y;
}

function drawKeyNumbers(doc, y, margin, pageW, keyNumbers) {
  y = drawSectionHeader(doc, "Key Numbers", y, margin, pageW);
  const colGap = 24;
  const colW = (pageW - margin * 2 - colGap) / 2;
  const rowH = 50;
  for (let i = 0; i < keyNumbers.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x  = margin + col * (colW + colGap);
    const yy = y + row * rowH;
    const { label, value } = keyNumbers[i];

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(latin1Safe(label.toUpperCase()), x, yy);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...INK);
    doc.text(latin1Safe(String(value)), x, yy + 22);
  }
  return y + rowH * Math.ceil(keyNumbers.length / 2) + 6;
}

function drawTable(doc, y, margin, pageW, rows, opts = {}) {
  const lineH = opts.lineH || 16;
  const labelW = opts.labelW || 240;
  const valueX = margin + labelW;
  for (const r of rows) {
    y = ensureSpace(doc, y, lineH, margin, pageW);
    const isTotal = r.bold || r.total;
    doc.setFont("helvetica", isTotal ? "bold" : "normal");
    doc.setFontSize(isTotal ? 11 : 10);
    doc.setTextColor(isTotal ? INK[0] : INK2[0], isTotal ? INK[1] : INK2[1], isTotal ? INK[2] : INK2[2]);
    doc.text(latin1Safe(String(r.label || "")), margin, y);
    if (r.value !== undefined) {
      const v = String(r.value);
      const w = doc.getTextWidth(v);
      doc.text(latin1Safe(v), pageW - margin - w, y);
    }
    if (r.right !== undefined) {
      const v = String(r.right);
      const w = doc.getTextWidth(v);
      doc.text(latin1Safe(v), valueX + 80 - w, y);
    }
    if (isTotal) {
      doc.setDrawColor(...RULE);
      doc.setLineWidth(0.4);
      doc.line(margin, y + 3, pageW - margin, y + 3);
    }
    y += lineH;
  }
  return y + 4;
}

function drawPnL(doc, y, margin, pageW, pnl) {
  y = ensureSpace(doc, y, 200, margin, pageW);
  y = drawSectionHeader(doc, "Profit & Loss", y, margin, pageW);
  const rows = [
    { label: "Revenue (MTD)",            value: fmtINR(pnl.revenue) },
    { label: "  Less: Raw Material",     value: fmtINR(pnl.raw_material) },
    { label: "  Less: Direct Labour",    value: fmtINR(pnl.direct_labour) },
    { label: "  Less: Overhead",         value: fmtINR(pnl.overhead) },
    { label: "Gross Profit",             value: fmtINR(pnl.gross_profit),    bold: true },
    { label: `Gross Margin %`,           value: fmtPct(pnl.gross_margin_pct) },
    { label: "  Less: Operating Expenses", value: fmtINR(pnl.opex) },
    { label: "Net Profit",               value: fmtINR(pnl.net_profit),      bold: true },
    { label: "Net Margin %",             value: fmtPct(pnl.net_margin_pct) },
  ];
  return drawTable(doc, y, margin, pageW, rows);
}

function drawCashFlow(doc, y, margin, pageW, cf) {
  y = ensureSpace(doc, y, 160, margin, pageW);
  y = drawSectionHeader(doc, "Cash Flow", y, margin, pageW);
  const rows = [
    { label: "Opening Cash",   value: fmtINR(cf.opening_cash) },
    { label: "  Inflows (Revenue collected)",  value: fmtINR(cf.inflows) },
    { label: "  Outflows (Expenses paid)",     value: fmtINR(cf.outflows) },
    { label: "Closing Cash",   value: fmtINR(cf.closing_cash), bold: true },
    { label: "Cash Runway",    value: cf.runway_days != null ? `${cf.runway_days} days` : DASH },
    { label: "GST Payable",    value: fmtINR(cf.gst_payable) },
  ];
  return drawTable(doc, y, margin, pageW, rows);
}

function drawInventory(doc, y, margin, pageW, inv) {
  y = ensureSpace(doc, y, 160, margin, pageW);
  y = drawSectionHeader(doc, "Inventory", y, margin, pageW);

  if (!inv.items || inv.items.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text(latin1Safe("No inventory data captured yet — connect Tally or upload a stock sheet to populate this section."), margin, y);
    return y + 22;
  }

  // Header row
  const cols = [
    { label: "Item",        x: margin,                              align: "left"  },
    { label: "Qty",         x: margin + 220,                        align: "right" },
    { label: "Value",       x: margin + 320,                        align: "right" },
    { label: "Days cover",  x: pageW - margin,                      align: "right" },
  ];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  for (const c of cols) {
    if (c.align === "right") {
      const w = doc.getTextWidth(c.label.toUpperCase());
      doc.text(latin1Safe(c.label.toUpperCase()), c.x - w, y);
    } else {
      doc.text(latin1Safe(c.label.toUpperCase()), c.x, y);
    }
  }
  y += 6;
  doc.setDrawColor(...RULE);
  doc.line(margin, y, pageW - margin, y);
  y += 14;

  // Body
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const it of inv.items) {
    y = ensureSpace(doc, y, 16, margin, pageW);
    doc.setTextColor(...INK2);
    doc.text(latin1Safe(String(it.name || DASH).slice(0), 36), margin, y);

    const qty = it.qty != null ? `${fmtNum(it.qty)}${it.unit ? " " + it.unit : ""}` : DASH;
    const qtyW = doc.getTextWidth(qty);
    doc.text(latin1Safe(qty), margin + 220 - qtyW, y);

    const val = fmtINR(it.value);
    const valW = doc.getTextWidth(val);
    doc.text(latin1Safe(val), margin + 320 - valW, y);

    const dc = it.days_cover != null ? `${it.days_cover} d` : DASH;
    if (it.days_cover != null && it.days_cover <= 7) {
      doc.setTextColor(...ROSE);
    }
    const dcW = doc.getTextWidth(dc);
    doc.text(latin1Safe(dc), pageW - margin - dcW, y);
    doc.setTextColor(...INK2);

    y += 14;
  }

  // Total row
  y += 4;
  doc.setDrawColor(...RULE);
  doc.line(margin, y, pageW - margin, y);
  y += 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text(latin1Safe("Total stock value"), margin, y);
  if (inv.total_value != null) {
    const v = fmtINR(inv.total_value);
    const vw = doc.getTextWidth(v);
    doc.text(latin1Safe(v), margin + 320 - vw, y);
  }
  if (inv.days_cover_avg != null) {
    const d = `${inv.days_cover_avg} d avg`;
    const dw = doc.getTextWidth(d);
    doc.text(latin1Safe(d), pageW - margin - dw, y);
  }
  return y + 14;
}

function drawPriorityActions(doc, y, margin, pageW, actions) {
  if (!actions || actions.length === 0) return y;
  y = ensureSpace(doc, y, 160, margin, pageW);
  y = drawSectionHeader(doc, "Priority Actions", y, margin, pageW);

  for (const a of actions) {
    y = ensureSpace(doc, y, 36, margin, pageW);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text(latin1Safe(`-  ${a.title}`), margin, y);
    y += 13;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...INK2);
    const wrapped = doc.splitTextToSize(latin1Safe(a.detail || ""), pageW - margin * 2 - 16);
    doc.text(latin1Safe(wrapped), margin + 12, y);
    y += wrapped.length * 12 + 8;
  }
  return y;
}

function safeFilename(company) {
  const slug = String(company || "Business")
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || "Business";
  return `${slug}-MIS-${currentPeriod().replace(" ", "-")}.pdf`;
}

// ---------------------------------------------------------------------------
// Public: generate the PDF.
// ---------------------------------------------------------------------------
export function generateMisPdf(data) {
  if (!data || typeof data !== "object") {
    throw new Error("generateMisPdf requires a data object built via buildMisData().");
  }

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = 56;

  // Logo + date
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...NAVY);
  doc.text(latin1Safe("C-ORE"), margin, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const dateStr = todayLabel();
  const dateW = doc.getTextWidth(dateStr);
  doc.text(latin1Safe(dateStr), pageW - margin - dateW, y);

  y += 18;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);

  // Company + period
  y += 28;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...INK);
  doc.text(latin1Safe(String(data.company_name || "Your business")), margin, y);

  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...INK2);
  doc.text(latin1Safe(String(data.period || currentPeriod())), margin, y);

  // Confidence chip on the right
  if (data.confidence_pct != null) {
    const chip = `Data confidence  ${data.confidence_pct}%`;
    const chipW = doc.getTextWidth(chip) + 14;
    const chipX = pageW - margin - chipW;
    const chipY = y - 11;
    doc.setDrawColor(...RULE);
    doc.setFillColor(247, 250, 252);
    doc.roundedRect(chipX, chipY, chipW, 18, 9, 9, "FD");
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    doc.text(latin1Safe(chip), chipX + 7, chipY + 12);
  }

  y += 28;
  y = drawKeyNumbers(doc, y, margin, pageW, data.key_numbers || []);
  y += 6;
  y = drawPnL(doc, y, margin, pageW, data.pnl || {});
  y += 6;
  y = drawCashFlow(doc, y, margin, pageW, data.cash_flow || {});
  y += 6;
  y = drawInventory(doc, y, margin, pageW, data.inventory || {});
  if (data.priority_actions && data.priority_actions.length > 0) {
    y += 6;
    y = drawPriorityActions(doc, y, margin, pageW, data.priority_actions);
  }

  // Footer on the last page
  const footerY = doc.internal.pageSize.getHeight() - 32;
  doc.setDrawColor(...RULE);
  doc.line(margin, footerY - 10, pageW - margin, footerY - 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(latin1Safe(`Generated by C-ORE on ${todayLabel()}`), margin, footerY);
  const meta = data.is_demo
    ? "Demo profile - sign up to download your real MIS"
    : "Confidential - For internal use only";
  const metaW = doc.getTextWidth(meta);
  doc.text(latin1Safe(meta), pageW - margin - metaW, footerY);

  doc.save(safeFilename(data.company_name));
}
